import { queryKeys } from '@/lib/query-keys';
import { storage } from '@/lib/storage';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useCurrentOrganization } from './useOrganization';

export function useDefaultSite() {
  const { data: organization, isLoading, isError } = useCurrentOrganization();
  const queryClient = useQueryClient();
  const [currentSiteId, setCurrentSiteId] = useState<string | null>(null);

  const hasRestrictions =
    organization?.totalSitesCount != null &&
    organization.sites.length < organization.totalSitesCount;

  // Initialize the current site when organization data is available
  useEffect(() => {
    if (organization?.sites?.length) {
      const savedSiteId = storage.get('defaultSite');
      const validSite = organization.sites.find(
        (site) => site.id === savedSiteId
      );

      if (validSite) {
        setCurrentSiteId(validSite.id);
      } else if (hasRestrictions) {
        // User has restrictions and no valid saved site: auto-select first allowed site
        setCurrentSiteId(organization.sites[0].id);
      } else {
        setCurrentSiteId(null);
      }
    }
  }, [organization, hasRestrictions]);

  const handleSiteChange = async (siteId: string | null, redirectTo?: string) => {
    storage.set('defaultSite', siteId || '');
    setCurrentSiteId(siteId);

    // Clear the cache
    queryClient.removeQueries({ queryKey: queryKeys.lines.root() });

    // Force a navigation to ensure everything is fresh
    if (redirectTo) {
      window.location.href = redirectTo;
    } else {
      window.location.reload();
    }
  };

  const currentSite = organization?.sites?.find(
    (site) => site.id === currentSiteId
  );

  return {
    currentSite,
    handleSiteChange,
    organization,
    isLoading,
    isError,
  };
}
